/**
 * Kind-discriminated `[scout-internal-event]` protocol for Scout HITL
 * between-turn proposals.
 *
 * The UI auto-sends a synthetic user message after the user resolves an
 * inline confirmation card. Scout's next turn reads the event; the chat
 * renderer hides the raw text so the user doesn't see their click rephrased
 * as a chat bubble.
 *
 * Wire format:
 *   `[scout-internal-event] <kind>/<confirmed|declined> proposalId=<id>: <detail>`
 *
 * **Why a text-prefix sentinel and not a typed enum field on the message:**
 * `ScoutMessagePart` is persisted server-side and shared across many ingest
 * paths; adding an enum column for "synthetic event kind" would require a
 * schema migration plus updates to every materializer. The prefix approach
 * keeps the change contained to client + prompt and round-trips cleanly
 * through the existing string-typed message body. The trade-off is fragility
 * (if anyone ever types the exact prefix, they'd disappear from the UI), so
 * the prefix is intentionally implausible-looking.
 */

/**
 * Minimal structural shape these helpers need from a chat message. Keeps the
 * helper independent of which `ScoutChatMessage` flavor a caller uses (the
 * `hooks/types` strict version vs. the `Chat/message/scoutChatMessage`
 * optional-content override).
 */
export type ChatMessageLike = {
  role: "system" | "user" | "assistant";
  parts: ReadonlyArray<{ type: string; text?: string | null; url?: string | null }>;
};

export const SCOUT_INTERNAL_EVENT_PREFIX = "[scout-internal-event]";

/** Resolution status shared by every proposal kind. */
export type ScoutInternalEventStatus = "confirmed" | "declined";

/**
 * Discriminator for which HITL proposal produced the synthetic continue
 * signal. Add new kinds here when wiring a new approval card.
 */
export type ScoutInternalEventKind =
  | "document-library-proposal"
  | "large-file-full-analysis"
  | "data-change-proposal";

export type ScoutInternalEvent = {
  kind: ScoutInternalEventKind;
  status: ScoutInternalEventStatus;
  proposalId: string;
};

const SCOUT_PRIVATE_DOCUMENT_EXTRACTION_URL_PREFIX = "encamp-private-document-extraction://";

const SCOUT_INTERNAL_EVENT_KINDS = [
  "document-library-proposal",
  "large-file-full-analysis",
  "data-change-proposal",
] as const satisfies ReadonlyArray<ScoutInternalEventKind>;

const SCOUT_INTERNAL_EVENT_KIND_ALTERNATION = SCOUT_INTERNAL_EVENT_KINDS.join("|");

/** Matches the header line; capture groups: kind, status, proposalId. */
const SCOUT_INTERNAL_EVENT_PATTERN = new RegExp(
  `^\\[scout-internal-event\\]\\s+(${SCOUT_INTERNAL_EVENT_KIND_ALTERNATION})/(confirmed|declined)\\s+proposalId=([^\\s:]+)`
);

function hasExtractionWatchFilePart(message: ChatMessageLike): boolean {
  return message.parts.some(
    (part) =>
      part.type === "file" &&
      typeof part.url === "string" &&
      part.url.startsWith(SCOUT_PRIVATE_DOCUMENT_EXTRACTION_URL_PREFIX)
  );
}

function isScoutInternalEventKind(value: string): value is ScoutInternalEventKind {
  return SCOUT_INTERNAL_EVENT_KINDS.some((kind) => kind === value);
}

/**
 * Build the synthetic user message text for a proposal resolution.
 * Kind-specific copy belongs in `detail`; this helper owns only the
 * kind-discriminated header line.
 */
export function buildScoutInternalEventMessage(input: {
  kind: ScoutInternalEventKind;
  status: ScoutInternalEventStatus;
  proposalId: string;
  /** Human-readable body after the colon (no leading space needed). */
  detail: string;
}): string {
  return (
    `${SCOUT_INTERNAL_EVENT_PREFIX} ${input.kind}/${input.status} ` +
    `proposalId=${input.proposalId}: ${input.detail}`
  );
}

/**
 * Parse a single text line into a structured internal event, or null if the
 * line is not a recognized `[scout-internal-event]` header.
 */
export function parseScoutInternalEventLine(text: string): ScoutInternalEvent | null {
  const match = text.trimStart().match(SCOUT_INTERNAL_EVENT_PATTERN);
  if (!match) return null;
  const kind = match[1];
  const statusToken = match[2];
  const proposalId = match[3];
  if (!kind || !statusToken || !proposalId || !isScoutInternalEventKind(kind)) {
    return null;
  }
  const status: ScoutInternalEventStatus = statusToken === "confirmed" ? "confirmed" : "declined";
  return { kind, status, proposalId };
}

/**
 * Scan persisted conversation history for synthetic proposal events and
 * return `proposalId -> status` for the given kind (or all kinds when
 * omitted). Lets approval cards stay in their "responded" state after a
 * refresh without a dedicated DB column — the responded record is the
 * synthetic user message itself.
 */
export function parseRespondedScoutInternalEventsFromMessages(
  messages: ReadonlyArray<ChatMessageLike>,
  kind?: ScoutInternalEventKind
): Record<string, ScoutInternalEventStatus> {
  const responded: Record<string, ScoutInternalEventStatus> = {};
  for (const message of messages) {
    if (message.role !== "user") continue;
    for (const part of message.parts) {
      if (part.type !== "text" || typeof part.text !== "string") continue;
      // Accept-all / Reject-all may pack multiple event lines into one text
      // part; scan each line so every proposalId rehydrates.
      for (const line of part.text.split("\n")) {
        const event = parseScoutInternalEventLine(line);
        if (!event) continue;
        if (kind !== undefined && event.kind !== kind) continue;
        responded[event.proposalId] = event.status;
      }
    }
  }
  return responded;
}

/**
 * Returns true if the chat UI should drop the whole message from rendering.
 *
 * Synthetic confirm messages for large-PDF ingest carry `extraction-watch`
 * file parts; those messages must STAY visible because `UserMessage` reads
 * the file parts to render the inline extraction progress card. The bubble
 * text is stripped separately by `stripScoutInternalEventLineFromText`, so
 * what the user sees is just the progress card.
 *
 * Synthetic decline messages and small-doc confirms have no file parts and
 * nothing useful to render — those we drop entirely.
 */
export function isSyntheticInternalEventMessage(message: ChatMessageLike): boolean {
  if (message.role !== "user") return false;
  const hasSentinelText = message.parts.some(
    (part) => part.type === "text" && part.text?.trimStart().startsWith(SCOUT_INTERNAL_EVENT_PREFIX)
  );
  if (!hasSentinelText) return false;
  return !hasExtractionWatchFilePart(message);
}

/**
 * Strips the `[scout-internal-event] …` sentinel line from a text part. Used
 * by `UserMessage` so a synthetic confirm message with extraction-watch
 * attachments renders only the progress card — never the raw event text.
 */
export function stripScoutInternalEventLineFromText(text: string | null | undefined): string {
  if (!text) return "";
  return text.replace(/^\[scout-internal-event\][^\n]*\n?/gm, "").trim();
}

/**
 * `[scout-citation]` sentinel for in-page starter questions.
 *
 * Regulation citations are long runs of letters, numbers and section marks;
 * inlined into the question they swamp it. The trigger appends them on their
 * own line and the bubble renders them as a caption under the question.
 *
 * Same text-sentinel approach as the event protocol above, and for the same
 * reason — the message body is a persisted string shared across ingest paths,
 * so a marker round-trips without a schema change. Unlike an internal event
 * this line is display metadata only: the message stays visible, and Scout
 * still reads the citation as part of the prompt.
 */
export const SCOUT_QUESTION_CITATION_PREFIX = "[scout-citation]";

/** The line a trigger appends to its question. */
export function buildScoutQuestionCitationLine(citation: string): string {
  return `${SCOUT_QUESTION_CITATION_PREFIX} ${citation}`;
}

/**
 * Split a user text part into the question and its citation label. Text with
 * no sentinel comes back unchanged with an undefined citation.
 */
export function splitScoutQuestionCitation(text: string | null | undefined): {
  text: string;
  citation: string | undefined;
} {
  if (!text) {
    return { text: "", citation: undefined };
  }

  const citations: string[] = [];
  const remaining: string[] = [];
  for (const line of text.split("\n")) {
    const trimmed = line.trimStart();
    if (trimmed.startsWith(SCOUT_QUESTION_CITATION_PREFIX)) {
      citations.push(trimmed.slice(SCOUT_QUESTION_CITATION_PREFIX.length).trim());
      continue;
    }
    remaining.push(line);
  }

  const citation = citations.filter((line) => line.length > 0).join("; ");
  return { text: remaining.join("\n").trim(), citation: citation || undefined };
}
